import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import dayjs from 'dayjs';
import useCommon from '@utils/hooks/common';
import styles from './styles';

interface TimerInterface {
  timeLimit: number;
}

const BUFFER = 2000; // Buffer is added to compensate for the time of table animation rendering

const NewTimer = forwardRef((props: TimerInterface, ref) => {
  const TIMER_STEP_MS = 100;
  const {classes} = useCommon({withStyles: styles});

  const [barTimeLimit, setBarTimeLimit] = useState(props.timeLimit ?? 60000);
  const [timePassed, setTimePassed] = useState<number>(0);
  const [isCountingDown, setIsCountingDown] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const timeLeft = barTimeLimit - timePassed;

  const getTimerColor = (timeLeftLocal: number, barTimeLimitLocal: number) => {
    if (isHidden || timeLeftLocal <= 0) return 'transparent';
    if (timeLeftLocal <= barTimeLimitLocal * 0.33) return '#FF7766';
    if (timeLeftLocal <= barTimeLimitLocal * 0.66) return '#FBED21';
    return '#91F27F';
  };

  const getWidthPercentage = (timePassedLocal: number, barTimeLimitLocal: number) => {
    if (timePassedLocal <= BUFFER) return 100;
    return ((barTimeLimitLocal - timePassedLocal) / (barTimeLimitLocal - BUFFER)) * 100;
  };

  const drawTimer = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const widthPercentage = getWidthPercentage(timePassed, barTimeLimit);
    const timerColor = getTimerColor(timeLeft, barTimeLimit);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw timer bar
    ctx.fillStyle = timerColor;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo((canvas.width * widthPercentage) / 100, 0);
    ctx.lineTo((canvas.width * widthPercentage) / 100, canvas.height);
    ctx.lineTo(0, canvas.height);
    ctx.closePath();
    ctx.fill();

    // Apply border radius
    if (widthPercentage <= 96) {
      ctx.beginPath();
      ctx.moveTo(0, 5);
      ctx.arcTo(0, 0, 5, 0, 5);
      ctx.arcTo(
        (canvas.width * widthPercentage) / 100,
        0,
        (canvas.width * widthPercentage) / 100,
        5,
        0
      );
      ctx.arcTo(
        (canvas.width * widthPercentage) / 100,
        canvas.height,
        (canvas.width * widthPercentage) / 100 - 5,
        canvas.height,
        0
      );
      ctx.arcTo(0, canvas.height, 0, canvas.height - 5, 5);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(0, 3);
      ctx.arcTo(0, 0, 3, 0, 3);
      ctx.arcTo(canvas.width, 0, canvas.width, 3, 3);
      ctx.arcTo(canvas.width, canvas.height, canvas.width - 3, canvas.height, 3);
      ctx.arcTo(0, canvas.height, 0, canvas.height - 3, 3);
      ctx.closePath();
      ctx.fill();
    }
  };

  useEffect(() => {
    drawTimer();
    if (timeLeft < 0) {
      clearInterval(timerIntervalRef.current!);
    }
  }, [timePassed]);

  useEffect(() => {
    return () => {
      clearInterval(timerIntervalRef.current!);
      setTimePassed(0);
    };
  }, []);

  const startTimer = () => {
    timerIntervalRef.current = setInterval(() => {
      setTimePassed(prevTimePassed => prevTimePassed + TIMER_STEP_MS);
    }, TIMER_STEP_MS);
  };

  useImperativeHandle(
    ref,
    () => ({
      stopTimer: () => {
        clearInterval(timerIntervalRef.current!);
        setIsCountingDown(false);
      },
      resetTimer: (currentPlayerActionExpireAt: any, currentPlayerActionTotalDuration: number) => {
        const timeLeftAfterReset = dayjs(currentPlayerActionExpireAt).diff(dayjs());
        setBarTimeLimit(currentPlayerActionTotalDuration * 1000);
        setTimePassed(currentPlayerActionTotalDuration * 1000 - timeLeftAfterReset);
      },
      startTimer: () => {
        setIsCountingDown(true);
        startTimer();
      },
      hideTimer: () => {
        setIsHidden(true);
      },
      unhideTimer: () => {
        setIsHidden(false);
      },
    }),
    [barTimeLimit]
  );

  return (
    <div className={classes.flashingBorder}>
      <canvas
        ref={canvasRef}
        className={classes.timerLayer}
        style={{
          width: '100%',
          height: '100%',
          transition: isCountingDown ? '0s linear' : '1s linear',
        }}
      />
    </div>
  );
});

NewTimer.displayName = 'NewTimer';

export default NewTimer;
